import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import zoharImg from '../images/הבית של זוהר 25.webp';
import s from './SketchToReal.module.css';

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  { num: '01', title: 'פגישת היכרות', desc: 'נשב ביחד, נדבר על החזון שלכם, על אורח החיים, על מה חשוב לכם בבית. מכאן מתחיל הסיפור.' },
  { num: '02', title: 'תכנון ראשוני', desc: 'בחינת האפשרויות השונות והצגת חלופות תכנון. ההעמדה בשטח, חלוקת המרחב, היחסים בין החללים ובחינת תלת המימד - תוך דו-שיח בינינו עד לדיוק התוכנית המושלמת עבורכם.' },
  { num: '03', title: 'תכנון מפורט', desc: 'ירידה לפרטים תכנונית וביצועית - תוכניות אדריכליות מפורטות לביצוע, תוכניות חשמל ותקשורת, מיזוג, אינסטלציה, פרטי בניין, פתחים, מדרגות וכל פרט העולה מן התכנון.' },
  { num: '04', title: 'עיצוב פנים ובחירות', desc: 'השלמת כל הבחירות שמרכיבות את הבית. חומרי הגמר, גוונים, גימורים, מטבח, חדרי רחצה, תאורה, נגרות מותאמת אישית וכל מה שנדרש להשלמת התמונה המלאה.' },
  { num: '05', title: 'ליווי הבנייה בשטח', desc: 'ליווי ופיקוח אדריכלי עליון בזמן הבנייה לווידוא יישום התוכנית, הדרכת בעלי המקצוע ופתרון בעיות בשטח - בתיאום מלא עם מפקח הבנייה, הקבלנים והיועצים, עד לסיום מלא של הבנייה וכניסה לבית בשעה טובה.' },
];

export default function SketchToReal({ onOpenLead }) {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const sketchImgRef = useRef(null);
  const realImgRef = useRef(null);
  const stepsBarRef = useRef(null);
  const stepsContentRef = useRef(null);
  const ctaContentRef = useRef(null);
  const [sketchSrc, setSketchSrc] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Generate sketch image once on mount
  useEffect(() => {
    const img = new Image();

    img.onload = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      // Draw image to offscreen canvas with cover crop
      const tmp = document.createElement('canvas');
      tmp.width = w; tmp.height = h;
      const tCtx = tmp.getContext('2d');
      const ir = img.width / img.height;
      const cr = w / h;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (ir > cr) { sw = img.height * cr; sx = (img.width - sw) / 2; }
      else { sh = img.width / cr; sy = (img.height - sh) / 2; }
      tCtx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);

      // Edge detection
      const realData = tCtx.getImageData(0, 0, w, h);
      const rd = realData.data;
      const gray = new Float32Array(w * h);
      for (let i = 0; i < w * h; i++) {
        gray[i] = 0.299 * rd[i * 4] + 0.587 * rd[i * 4 + 1] + 0.114 * rd[i * 4 + 2];
      }

      const edges = new Float32Array(w * h);
      let maxE = 0;
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          const gx = -gray[(y-1)*w+(x-1)] + gray[(y-1)*w+(x+1)] - 2*gray[y*w+(x-1)] + 2*gray[y*w+(x+1)] - gray[(y+1)*w+(x-1)] + gray[(y+1)*w+(x+1)];
          const gy = -gray[(y-1)*w+(x-1)] - 2*gray[(y-1)*w+x] - gray[(y-1)*w+(x+1)] + gray[(y+1)*w+(x-1)] + 2*gray[(y+1)*w+x] + gray[(y+1)*w+(x+1)];
          let e = Math.sqrt(gx * gx + gy * gy);
          const lap = Math.abs(-4 * gray[i] + gray[(y-1)*w+x] + gray[(y+1)*w+x] + gray[y*w+(x-1)] + gray[y*w+(x+1)]);
          e = Math.max(e, lap * 1.5);
          edges[i] = e;
          if (e > maxE) maxE = e;
        }
      }

      // Draw sketch: cream bg + dark lines
      const sketchCanvas = document.createElement('canvas');
      sketchCanvas.width = w; sketchCanvas.height = h;
      const sCtx = sketchCanvas.getContext('2d');
      const out = sCtx.createImageData(w, h);
      const od = out.data;
      for (let i = 0; i < w * h; i++) {
        const e = edges[i] / maxE;
        const line = e > 0.03 ? Math.min(1, e * 4) : 0;
        od[i * 4]     = Math.round(245 - 215 * line);
        od[i * 4 + 1] = Math.round(240 - 212 * line);
        od[i * 4 + 2] = Math.round(234 - 210 * line);
        od[i * 4 + 3] = 255;
      }
      sCtx.putImageData(out, 0, 0);

      // Convert to static image - immune to canvas clearing on pin
      setSketchSrc(sketchCanvas.toDataURL('image/jpeg', 0.92));
    };

    img.src = zoharImg;
  }, []);

  // GSAP scroll animation - only after sketch is ready
  useEffect(() => {
    if (!sketchSrc) return;

    // Small delay to ensure img elements are rendered
    const raf = requestAnimationFrame(() => {
      const gsapCtx = gsap.context(() => {
        const vh = window.innerHeight;
        gsap.set(realImgRef.current, { opacity: 0 });
        if (ctaContentRef.current) gsap.set(ctaContentRef.current, { autoAlpha: 0, y: 30 });

        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top top',
          end: `+=${vh * 4}`,
          pin: pinRef.current,
          pinSpacing: true,
          scrub: 0.5,
          onEnter: () => {
            gsap.set(realImgRef.current, { visibility: 'visible', opacity: 0 });
          },
          onEnterBack: () => {
            gsap.set(realImgRef.current, { visibility: 'visible' });
          },
          onLeaveBack: () => {
            gsap.set(realImgRef.current, { opacity: 0, visibility: 'hidden' });
            if (stepsContentRef.current) gsap.set(stepsContentRef.current, { opacity: 1 });
            if (ctaContentRef.current) gsap.set(ctaContentRef.current, { autoAlpha: 0, y: 30 });
            setActiveStep(0);
          },
          onUpdate: (self) => {
            const p = self.progress;

            // Phase 1: sketch → photo + step advance (0 → 0.72)
            if (p <= 0.72) {
              const photoP = Math.min(1, p / 0.72);
              gsap.set(realImgRef.current, { opacity: photoP, visibility: 'visible' });
              setActiveStep(Math.min(STEPS.length - 1, Math.floor(photoP * STEPS.length)));
              if (stepsContentRef.current) gsap.set(stepsContentRef.current, { opacity: 1 });
              if (ctaContentRef.current) gsap.set(ctaContentRef.current, { autoAlpha: 0, y: 30 });
            }
            // Phase 2: content fades out, CTA fades in within the same frame (0.72 → 1.0)
            else {
              const swapP = (p - 0.72) / 0.28;
              gsap.set(realImgRef.current, { opacity: 1 });
              setActiveStep(STEPS.length - 1);

              if (stepsContentRef.current) {
                gsap.set(stepsContentRef.current, { opacity: Math.max(0, 1 - swapP * 1.4) });
              }
              if (ctaContentRef.current) {
                const ctaP = Math.max(0, Math.min(1, (swapP - 0.25) / 0.75));
                gsap.set(ctaContentRef.current, { autoAlpha: ctaP, y: 16 * (1 - ctaP) });
              }
            }
          },
        });
      }, sectionRef);

      return () => gsapCtx.revert();
    });

    return () => cancelAnimationFrame(raf);
  }, [sketchSrc]);

  return (
    <section className={s.section} ref={sectionRef} id="sketch-to-real">
      <div className={s.pin} ref={pinRef}>
        <div className={s.canvasWrap}>
          {/* Layer 1: Sketch as static image - won't get cleared on pin */}
          {sketchSrc && <img ref={sketchImgRef} src={sketchSrc} alt="" className={s.sketchImg} />}
          {/* Layer 2: Real photo - fades in on scroll */}
          <img ref={realImgRef} src={zoharImg} alt="" className={s.realPhoto} />
          <div className={s.overlay} />
        </div>

        <div className={s.header}>
          <h2 className={s.heading}>מהרעיון למציאות</h2>
        </div>

        {/* ═══ Floating framed step card ═══ */}
        <div className={s.stepsBar} ref={stepsBarRef}>
          <span className={s.stepCardCornerTL} />
          <span className={s.stepCardCornerTR} />
          <span className={s.stepCardCornerBL} />
          <span className={s.stepCardCornerBR} />

          <div className={s.stepsContent} ref={stepsContentRef}>
            <div className={s.stepCard} key={`step-${activeStep}`}>
              <span className={s.stepCardCount}>
                שלב {String(activeStep + 1).padStart(2, '0')} מתוך {String(STEPS.length).padStart(2, '0')}
              </span>
              <h3 className={s.stepCardTitle}>{STEPS[activeStep].title}</h3>
              <p className={s.stepCardDesc}>{STEPS[activeStep].desc}</p>
              <div className={s.stepCardPips}>
                {STEPS.map((_, i) => (
                  <span
                    key={i}
                    className={`${s.stepCardPip} ${i === activeStep ? s.stepCardPipOn : ''} ${i < activeStep ? s.stepCardPipDone : ''}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className={s.ctaContent} ref={ctaContentRef}>
            <h2 className={s.ctaHeading}>בואו נדבר על הפרויקט שלכם</h2>
            <button type="button" onClick={onOpenLead} className={s.ctaBtn}>
              <span>לשיחת היכרות לחצו כאן</span><span className={s.ctaArrow}>←</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
